/**
 * media-audio-track.js — the SOUND of a video, as the audio file it already is.
 *
 * Audio tools take videos: a phone screen recording to cut a ringtone from, a
 * clip whose voice needs louder. They work on the sound, and what they deliver
 * is an audio file (the server lane of /trim has always run `-vn`). What they
 * lacked was that sound in the BROWSER without re-encoding it:
 *   • «source» export of an .mp4 had no container to go to — mp4/mov/mkv/avi are
 *     input-only in config/formats.php — so /trim sent every video to its server
 *     lane, whose ceiling is 50 MB and one run a day. Report of 30.09.2026:
 *     iPhone, 168 s screen recording, 79 MB, «Не загружается файл больше 1
 *     минуты»; September: 139 /trim sessions with a video over 50 MB, 25 of them
 *     downloaded anything.
 *   • where a tool did hand the video to FFmpeg-WASM for an audio output,
 *     nothing dropped the picture: an .m4a export from a 77 MB HEVC recording
 *     sat 300 s re-encoding frames and delivered nothing.
 *
 *   MediaAudioTrack.isVideoContainer(file) → bool
 *   await MediaAudioTrack.plan(file)
 *       → { codec: 'aac', ext: 'm4a', lane: 'remux'|'copy'|'encode' }
 *         read from the container header only (MediaAudioProbe), no FFmpeg.
 *   await MediaAudioTrack.extract(file, {slug})
 *       → { file: File (sound only, named <base>.<ext>), ext, codec, lane, ms }
 *       A second call for the same File returns the first call's promise.
 *       Throws an Error with `code` 'no-audio' when the container has no sound
 *       (read, not guessed), else the last lane's error.
 *
 * Lanes, first that answers:
 *   remux  — mp4 / mov / m4v / 3gp whose sound is AAC or ALAC: MP4TrackRemux
 *            keeps the `soun` trak. The File is references into the source
 *            (moov first, not one sample byte copied, no FFmpeg): the 77 MB
 *            recording above → a 2.7 MB .m4a in milliseconds, packets
 *            bit-identical to `ffmpeg -vn -c:a copy`.
 *   copy   — any other container / codec with an audio container of its own
 *            (NATURAL_EXT): FFmpeg `-map 0:a:0 -c:a copy`, the input
 *            WORKERFS-mounted (processStreaming), so the video is never copied.
 *   encode — sound with no container of its own (DTS, TrueHD, μ-law …) or a
 *            header nobody could read: AAC 192 kb/s in .m4a.
 *
 * ⚠️ Nothing here decodes: the tool already has PCM from AudioDecoderRobust.
 * This is the FILE the export works on — «source» becomes the track as it is.
 */
(function () {
'use strict';
if (window.MediaAudioTrack) return;

// Containers that carry pictures. .webm/.ogg carry sound alone just as often —
// their MIME type decides (video/webm is a recording with a picture).
var VIDEO_EXT = /\.(mp4|m4v|mov|qt|3gp|3g2|mkv|avi|ts|mts|m2ts|wmv|asf|mpg|mpeg|vob|flv|ogv)$/i;
var AUDIO_EXT = /\.(mp3|m4a|m4b|m4r|m4p|aac|wav|wave|flac|ogg|oga|opus|weba|aiff|aif|aifc|wma|amr|awb|caf|ac3|eac3|mka|mp2|mpga)$/i;
var MP4_FAMILY = /\.(mp4|m4v|mov|qt|3gp|3g2)$/i;

// The audio container a codec already lives in — named by MediaAudioProbe's
// codec ids (aac, ac-3 …) and by FFmpeg's (pcm_s16le, wmav2 …).
var NATURAL_EXT = {
    'aac': 'm4a', 'alac': 'm4a', 'mp3': 'mp3', 'mp2': 'mp2', 'opus': 'opus',
    'vorbis': 'ogg', 'flac': 'flac', 'pcm': 'wav', 'ac-3': 'ac3', 'e-ac-3': 'eac3',
    'ac3': 'ac3', 'eac3': 'eac3', 'pcm_s16le': 'wav', 'pcm_s24le': 'wav', 'pcm_u8': 'wav',
    'wmav1': 'wma', 'wmav2': 'wma'
};
var MIME = { m4a: 'audio/mp4', mp3: 'audio/mpeg', mp2: 'audio/mpeg', opus: 'audio/ogg', ogg: 'audio/ogg',
    flac: 'audio/flac', wav: 'audio/wav', ac3: 'audio/ac3', eac3: 'audio/eac3', wma: 'audio/x-ms-wma' };

var cache = (typeof WeakMap !== 'undefined') ? new WeakMap() : null;
// copyOnly answers live apart: an encoded track from another caller is not a
// copy, and a copy-only refusal must not stop a caller that accepts encoding.
var copyCache = (typeof WeakMap !== 'undefined') ? new WeakMap() : null;

function typed(code, msg) { var e = new Error('media-audio-track: ' + (msg || code)); e.code = code; return e; }
function baseName(file) { return String((file && file.name) || 'audio').replace(/\.[^.]+$/, '') || 'audio'; }
function now() { return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now(); }
function ae(p) { try { if (window.__ae) window.__ae('feature_use', p); } catch (_) {} }

function isVideoContainer(file) {
    if (!file) return false;
    var name = String(file.name || '');
    if (AUDIO_EXT.test(name)) return false;
    if (VIDEO_EXT.test(name)) return true;
    return /^video\//i.test(String(file.type || ''));
}

// A module this one leans on, loaded when first needed (a page that never gets a
// video never pays for it). __jsSrc gives the versioned URL, so a returning
// visitor is never pinned to an old copy by the immutable cache.
var loading = {};
function need(global, path) {
    if (window[global]) return Promise.resolve(window[global]);
    if (loading[global]) return loading[global];
    var url = (typeof window.__jsSrc === 'function') ? window.__jsSrc(path) : path;
    loading[global] = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        var n = document.querySelector('script[nonce]');
        if (n && n.nonce) s.setAttribute('nonce', n.nonce);
        s.src = url;
        s.onload = function () { if (window[global]) resolve(window[global]); else { loading[global] = null; reject(typed('no-module', path + ' loaded without ' + global)); } };
        s.onerror = function () { loading[global] = null; reject(typed('no-module', path + ' failed to load')); };
        document.head.appendChild(s);
    });
    return loading[global];
}

/* Which sound track is «the sound of the video». Not always the first: an
   iPhone 16+ MOV with spatial audio carries `apac` next to the stereo AAC, and
   nobody but Apple decodes `apac` — taking track 0 there handed FFmpeg a stream
   it cannot open. Order of preference: a track the remux lane copies as it is
   (AAC, ALAC), then one with an audio container of its own, then any codec we
   know by name; `-1` = the probe did not list tracks (Matroska, unreadable). */
var REMUXABLE = { 'aac': 1, 'alac': 1 };
var UNPLAYABLE = { 'apac': 1 };
function pickTrack(tracks) {
    if (!tracks || !tracks.length) return -1;
    var i;
    for (i = 0; i < tracks.length; i++) if (REMUXABLE[tracks[i].codec]) return i;
    for (i = 0; i < tracks.length; i++) if (NATURAL_EXT[tracks[i].codec]) return i;
    for (i = 0; i < tracks.length; i++) if (tracks[i].codec && !UNPLAYABLE[tracks[i].codec]) return i;
    return 0;
}
function assign(a, b) { for (var k in b) if (Object.prototype.hasOwnProperty.call(b, k)) a[k] = b[k]; return a; }

async function plan(file) {
    var codec = '', hasAudio = null, tracks = null, chans = 0, rate = 0;
    try {
        var P = await need('MediaAudioProbe', '/js/media-audio-probe.js');
        var pr = await P.probe(file);
        hasAudio = pr ? pr.hasAudio : null;
        tracks = (pr && pr.tracks && pr.tracks.length) ? pr.tracks : null;
        codec = (pr && pr.codecs && pr.codecs[0]) ? String(pr.codecs[0]).toLowerCase() : '';
        // Matroska / WebM lists no tracks — its first sound track's own numbers
        chans = (pr && pr.channels) || 0;
        rate = (pr && pr.sampleRate) || 0;
    } catch (_) {}
    if (hasAudio === false) return { codec: '', ext: '', lane: 'none', hasAudio: false };
    var idx = pickTrack(tracks), trackId = 0;
    if (idx >= 0) { codec = String(tracks[idx].codec || '').toLowerCase(); trackId = tracks[idx].id || 0; }
    var at = { track: idx >= 0 ? idx : 0, trackId: trackId,
               channels: idx >= 0 ? (tracks[idx].channels || 0) : chans, sampleRate: idx >= 0 ? (tracks[idx].sampleRate || 0) : rate };
    var mp4 = MP4_FAMILY.test(String(file.name || '')) || /^video\/(mp4|quicktime)/i.test(String(file.type || ''));
    // An mp4 we could not read is AAC far more often than not; the remux lane
    // refuses by itself when the file is something else.
    if (mp4 && (codec === 'aac' || codec === 'alac' || codec === '')) {
        return assign({ codec: codec || 'aac', ext: 'm4a', lane: 'remux', hasAudio: hasAudio }, at);
    }
    var ext = NATURAL_EXT[codec] || '';
    return assign({ codec: codec, ext: ext || 'm4a', lane: ext ? 'copy' : 'encode', hasAudio: hasAudio }, at);
}

/* The audio file, made of references into the video:
   · ONLY the chosen track (keepTrackIds) — a second language or an `apac` field
     would ride along otherwise;
   · overheadBudget 0 — no picture bytes kept to save Blob parts. That budget
     exists for muting a video, where the gaps are single audio samples; here
     the gaps are video frames, so merging buys almost nothing and costs a
     fifth of the file: corpus 2026-10-08, FFmpeg-muxed 2 min — 3604 parts at 0
     against 3531 at 0.25 (+0.48 MB), 3 GB phone video — 4 parts either way;
   · ftyp `M4A ` for an ISO source (see MP4TrackRemux brand): a `3gp6`-branded
     .m4a did not open in Firefox. A QuickTime source keeps its own brand. */
async function remuxLane(file, p) {
    var R = await need('MP4TrackRemux', '/js/mp4-track-remux.js');
    var opts = { keepHandlers: ['soun'], mime: 'audio/mp4', overheadBudget: 0, brand: 'M4A ' };
    if (p && p.trackId) opts.keepTrackIds = [p.trackId];
    var r = await R.remux(file, opts);
    return new File([r.blob], baseName(file) + '.m4a', { type: 'audio/mp4', lastModified: Date.now() });
}

async function ffmpegLane(file, p, copyOnly) {
    var FP = window.FFmpegProcessor;
    if (!FP || typeof FP.processStreaming !== 'function') throw typed('no-engine', 'FFmpegProcessor is not on this page');
    if (typeof FP.init === 'function' && !(await FP.init())) throw typed('engine-init', 'FFmpeg did not start');
    var tries = [];
    var map = '0:a:' + ((p && p.track) || 0);     // the track plan() chose, not always the first
    if (p.lane === 'copy') tries.push({ ext: p.ext, lane: 'copy', args: ['-map', map, '-c:a', 'copy'] });
    if (!copyOnly) {
        if (p.codec === 'pcm') tries.push({ ext: 'wav', lane: 'encode', args: ['-map', map, '-c:a', 'pcm_s16le'] });
        tries.push({ ext: 'm4a', lane: 'encode', args: ['-map', map, '-c:a', 'aac', '-b:a', '192k'] });
    }
    if (!tries.length) throw typed('no-copy', 'the track comes out only by encoding');
    var last = null;
    for (var i = 0; i < tries.length; i++) {
        var t = tries[i];
        var out = 'mat_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6) + '.' + t.ext;
        try {
            var res = await FP.processStreaming(file, t.args, out);
            // An intermediate, not a result: keep the auto-tracker from calling it
            // a delivery (it re-checks the skip list when its 2 s debounce fires).
            try { if (window.__skipBlobAutoTrack) window.__skipBlobAutoTrack(res.blob); } catch (_) {}
            try { if (res.url) URL.revokeObjectURL(res.url); } catch (_) {}
            if (!res.blob || !res.blob.size) throw typed('empty-output', 'empty output');
            return { file: new File([res.blob], baseName(file) + '.' + t.ext, { type: MIME[t.ext] || 'audio/mp4', lastModified: Date.now() }), ext: t.ext, lane: t.lane };
        } catch (e) {
            last = e;
            if (window.__isUserCancel && window.__isUserCancel(e)) throw e;
        }
    }
    throw last || typed('no-lane', 'no lane produced the sound');
}

/* opts.copyOnly — the caller encodes the sound itself (/convert): a track that
   comes out only by ENCODING is refused (`no-copy`) instead of being encoded to
   AAC 192k here — that would be a second lossy generation, and the time of a
   whole encode spent on an intermediate. Remux and stream copy only. */
function extract(file, opts) {
    opts = opts || {};
    if (!file) return Promise.reject(typed('no-file', 'no file'));
    var copyOnly = opts.copyOnly === true;
    var jobs = copyOnly ? copyCache : cache;
    if (jobs && jobs.has(file)) return jobs.get(file);
    var t0 = now();
    var mb = Math.round((file.size || 0) / 1048576);
    var job = (async function () {
        var p = await plan(file);
        if (p.hasAudio === false) throw typed('no-audio', 'the container has no sound track');
        if (copyOnly && p.lane !== 'remux' && p.lane !== 'copy') throw typed('no-copy', 'the track comes out only by encoding');
        var done = null, remuxErr = '';
        if (p.lane === 'remux') {
            try { done = { file: await remuxLane(file, p), ext: 'm4a', lane: 'remux' }; }
            catch (e) { remuxErr = String((e && (e.remuxReason || e.code || e.message)) || e).slice(0, 60); }
        }
        if (!done) done = await ffmpegLane(file, p.lane === 'remux' ? { codec: p.codec, ext: 'm4a', lane: 'copy', track: p.track } : p, copyOnly);
        var out = { file: done.file, ext: done.ext, codec: p.codec, lane: done.lane, ms: Math.round(now() - t0),
                    track: p.track, channels: p.channels, sampleRate: p.sampleRate };
        ae({ slug: opts.slug || '', f: 'audio_track_extract', lane: out.lane, codec: out.codec, ext: out.ext, ms: out.ms, mb: mb,
             kb: Math.round(out.file.size / 1024), track: p.track || undefined, remux_err: remuxErr || undefined });
        return out;
    })();
    job.catch(function (e) {
        if (jobs) jobs.delete(file);   // a failed extraction may succeed on the next attempt
        // `no-copy` before any attempt is an answer (the codec needs encoding), not a failure
        if (e && e.code === 'no-copy') return;
        if (!(window.__isUserCancel && window.__isUserCancel(e))) {
            ae({ slug: opts.slug || '', f: 'audio_track_extract_failed', code: (e && e.code) || '', mb: mb,
                 err: String((e && e.message) || e).slice(0, 120) });
        }
    });
    if (jobs) jobs.set(file, job);
    return job;
}

window.MediaAudioTrack = { isVideoContainer: isVideoContainer, plan: plan, extract: extract, NATURAL_EXT: NATURAL_EXT };
})();
